import { Link, Radio, RadioGroup, Stack, Text } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';

import { PreferenceEditorSectionHeading } from '../components/PreferenceEditorSectionHeading';
import { UserPreferenceKey } from '../types';
import { PreferenceBrowserStorage } from '../utils/preference-session-storage';
import type {
  PreferenceEditorProps,
  PreferenceMeta,
} from './preferenceEditors';

export const meta: PreferenceMeta = {
  id: UserPreferenceKey.DataAdjustments,
  label: 'Data Adjustments',
};

export default function DataAdjustmentsEditor({
  affectedScope,
  onPreferenceUpdate,
  preferences,
  diff,
}: PreferenceEditorProps) {
  const [adjustment, setAdjustment] = useState<'adjusted' | 'unadjusted'>(
    'adjusted'
  );

  useEffect(() => {
    setAdjustment(
      preferences[UserPreferenceKey.DataAdjustments] ? 'unadjusted' : 'adjusted'
    );
  }, [preferences]);

  const onUpdate = useCallback(
    (value: 'adjusted' | 'unadjusted') => {
      const isRaw = value === 'unadjusted';
      onPreferenceUpdate?.(UserPreferenceKey.DataAdjustments, isRaw);
      setAdjustment(value);
      PreferenceBrowserStorage.update(
        affectedScope,
        UserPreferenceKey.DataAdjustments,
        isRaw
      );
    },
    [affectedScope, onPreferenceUpdate]
  );

  return (
    <>
      <PreferenceEditorSectionHeading
        title="Adjustment Type"
        indicateDiff={diff?.includes(meta.id) ?? false}
      />
      <RadioGroup
        value={adjustment}
        onChange={(val) => onUpdate(val as 'adjusted' | 'unadjusted')}
        colorScheme="green"
        size="sm"
        mt={3}
      >
        <Stack gap={3}>
          <Stack gap={1}>
            <Radio value="adjusted">Adjusted</Radio>
            <Text ml={5} fontSize="xs" color="gray.500" lineHeight="1.4">
              Adjusted data applies sampling weights to correct for
              representation bias (e.g., white-collar workers are
              overrepresented online) and uses a nowcasting model to counteract
              delays in workers updating their profiles after job changes.
            </Text>
          </Stack>
          <Stack gap={1}>
            <Radio value="unadjusted">Unadjusted</Radio>
            <Text ml={5} fontSize="xs" color="gray.500" lineHeight="1.4">
              Unadjusted data shows raw counts without statistical corrections
              for these biases. Learn more about{' '}
              <Link
                href="https://www.data-dictionary.reveliolabs.com/methodology.html#sampling-weights"
                isExternal
                color="lightBlue.600"
                textDecoration="underline"
              >
                Sampling Weights
              </Link>{' '}
              and{' '}
              <Link
                href="https://www.data-dictionary.reveliolabs.com/methodology.html#lags-in-reporting"
                isExternal
                color="lightBlue.600"
                textDecoration="underline"
              >
                Lags in Reporting
              </Link>
              .
            </Text>
          </Stack>
        </Stack>
      </RadioGroup>
    </>
  );
}
